import { PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { QueryClient } from 'react-query';
import { getContext, put, select, takeLatest } from 'redux-saga/effects';
import { AdvancedSetting } from 'api/advanced-settings/settings-types';
import { ProjectId } from 'api/models';
import { Project, ProjectPhase } from 'api/project';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import {
  filterTaskUpdates,
  formatDate,
  getParentStartDate,
  getPhaseList,
  getTaskList,
} from 'components/organisms/projects/project-plan/timeline/helpers';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { updateGanttDatesAction } from 'data/projects/actions';
import { Task } from 'data/tasks';
import {
  CreateDependencyActionPayload,
  dependencyCreatedAction,
  UpdateTaskActionPayload,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { selectTasksByProjectId } from 'data/tasks/selectors';
import { updateTasksDates } from 'data/tasks/slice';
import {
  getAlignPhaseDatesSetting,
  getQueryAdvancedSettingsConfigKey,
} from 'queries/advanced-settings/advanced-settings';
import { getProjectDetailQueryKey } from 'queries/common';
import {
  Entity,
  EntityDateShiftUpdate,
  EntityType,
  getDateShiftsForEntity,
} from 'utils/date-shift';
import { getProjectDetailUrl } from 'utils/urls';

export const updateProjectPhases = (
  projectId: number,
  updates: EntityDateShiftUpdate[],
  queryClient: QueryClient,
) => {
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));

  if (project) {
    const { projectPhases: phases } = project;
    const updatedPhases = phases.map((phase) => {
      const update = updates.find((u) => u.id === phase.projectPhaseId);

      if (update != null) {
        const { startDate, dueDate } = update;
        const _update = {} as Partial<ProjectPhase>;

        if (startDate) {
          _update.startDate = moment(startDate).format(API_DATE_FORMAT);
        }
        if (dueDate) {
          _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
        }

        return {
          ...phase,
          ..._update,
        };
      }

      return phase;
    });

    const queryKey = getProjectDetailQueryKey(ProjectId(projectId));

    queryClient.setQueryData<Project>(queryKey, () => ({
      ...project,
      projectPhases: updatedPhases,
    }));
  }
};

function* onUpdateTask({
  payload,
}: PayloadAction<UpdateTaskActionPayload>): Generator {
  const { taskId, projectId, data } = payload;
  const _projectId = ProjectId(projectId);

  /**
   * Patch fix to make date shifting work inside project detail
   * since phases and tasks for a project are pre-requisites for date shifting
   * which are available only inside project detail
   *
   * TODO: read the tasks data from query which is fetched on loading task pane
   * unlike redux store which is loaded only inside project detail route
   * Need a single source of truth.
   */
  if (!window.location.pathname.includes(getProjectDetailUrl(_projectId))) {
    return;
  }
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(_projectId);
  const ganttRef = (yield getContext('ganttRef')) as any;

  if (!project) {
    return;
  }
  const { startDate, dueDate } = data;

  /**
   * dates will be set as null on clearing dates
   * hence processing null values.
   */
  if (!(startDate || startDate === null) && !(dueDate || dueDate === null)) {
    return;
  }
  const { projectPhases } = project;
  const tasks = (yield select(selectTasksByProjectId(_projectId))) as Task[];

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);

  const currentEntity = taskList[taskId];
  const taskRecord = tasks.find((_task) => _task.taskId === taskId);

  const updatedTask: Entity = {
    id: taskId,
    type: EntityType.Task,
    depth: currentEntity.depth,
  };

  if (startDate) {
    updatedTask.startDate = formatDate(startDate);
    if (!taskRecord?.dueDate && !dueDate) {
      updatedTask.dueDate = formatDate(startDate);
    }
  }
  if (dueDate) {
    updatedTask.dueDate = formatDate(dueDate);
    if (!taskRecord?.startDate && !startDate) {
      updatedTask.startDate = formatDate(dueDate);
    }
  }
  /** Handle clearing dates */
  if (startDate === null && dueDate === null) {
    const parentStartDate = formatDate(
      getParentStartDate(taskId, {
        ...phaseList,
        ...taskList,
      }),
    );

    updatedTask.startDate = parentStartDate;
    updatedTask.dueDate = parentStartDate;
  } else if (startDate === null) {
    updatedTask.startDate = formatDate(dueDate || taskList[taskId].dueDate);
  } else if (dueDate === null) {
    updatedTask.dueDate = formatDate(startDate || taskList[taskId].startDate);
  }

  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);

  const [phaseUpdates, taskUpdates] = getDateShiftsForEntity(
    phaseList,
    taskList,
    updatedTask,
    {
      alignPhaseWithTask: alignPhaseDates,
    },
  );

  yield put(
    updateGanttDatesAction({
      phaseUpdates,
      taskUpdates,
      phaseList,
      taskList,
      projectPhases,
    }),
  );

  if (startDate || dueDate) {
    const scrollToDate = startDate ? new Date(startDate) : new Date(dueDate);

    ganttRef?.instance.scrollToDate(scrollToDate);
  }

  const filteredUpdates = filterTaskUpdates(taskUpdates, tasks);

  if (startDate) {
    filteredUpdates[taskId] = {
      ...filteredUpdates[taskId],
      startDate: taskUpdates[taskId].startDate,
    };
  }
  if (dueDate) {
    filteredUpdates[taskId] = {
      ...filteredUpdates[taskId],
      dueDate: taskUpdates[taskId].dueDate,
    };
  }
  yield put(updateTasksDates(Object.values(filteredUpdates)));
  updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
}

function* onDependencyCreated({
  payload,
}: PayloadAction<CreateDependencyActionPayload>): Generator {
  const { taskId, dependentTaskId, projectId } = payload;
  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));

  if (!project) {
    return;
  }

  const { projectPhases } = project;
  const tasks = (yield select(
    selectTasksByProjectId(ProjectId(projectId)),
  )) as Task[];

  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);

  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );

  const alignPhaseDates = getAlignPhaseDatesSetting(accountSettings);

  const [phaseUpdates, taskUpdates] = getDateShiftsForEntity(
    phaseList,
    taskList,
    {
      id: dependentTaskId,
      dependencyFor: [
        ...(taskList?.[dependentTaskId]?.dependencyFor || []),
        taskId,
      ],
      type: EntityType.Task,
      depth: taskList[dependentTaskId].depth,
    },
    {
      alignPhaseWithTask: alignPhaseDates,
    },
  );

  yield put(
    updateGanttDatesAction({
      phaseUpdates,
      taskUpdates,
      phaseList,
      taskList,
      projectPhases,
    }),
  );
  yield put(
    updateTasksDates(Object.values(filterTaskUpdates(taskUpdates, tasks))),
  );
  updateProjectPhases(projectId, Object.values(phaseUpdates), queryClient);
}

export const watchDateShiftSaga = function* (): Generator {
  yield takeLatest(updateTaskSagaAction.type, onUpdateTask);
  yield takeLatest(dependencyCreatedAction.type, onDependencyCreated);
};
